import React, { memo, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { cloneDeep, shuffle } from 'lodash';
import { create } from 'zustand';

import { Images } from '../../../constants/images';
import { getDistractorPools } from '../../../constants/MemoryGames/distractorPool';
import {
  getCandidateAnswerSlot,
  getPreFilledIndexes,
} from '../../../constants/MemoryGames/spellingChar';
import { SVGIconType } from '../../../constants/svgIcon';
import { colorForStroke } from '../../../constants/Theme/DefaultColors';
import { AppToastGameErrorOption } from '../../../constants/toast';
import { usePopup } from '../../../context';
import { TtsLocale, Uuid } from '../../../models/Base';
import {
  EvaluationType,
  GameController,
  GamePlayState,
} from '../../../models/GameControllerStore';
import {
  TextOrderingAnswer,
  TextOrderingSoundMode,
  TextOrderingUnit,
  TextOrderingUnitType,
} from '../../../models/GameStore';
import { TextEvaluationWord } from '../../../models/RecordStore';
import { InGameSoundEffects } from '../../../stores/GameControllerStore';
import { getCharacterCount, selectRandomWords } from '../../../utils/string';
import { PngIcon, SvgIcon } from '../../Molecules';
import { LeaveGamePopup } from '../../Popups';
import {
  AnswerCardData,
  AnswerCardStatus,
  GameBlankAnswerCardsGroup,
} from '../GameBlankAnswerCardsGroup';
import { TextOrderingGameMode } from '../GameQuestionPanel';

import styles from './MemoryGameMainGroup.module.css';

export type TextOrderingBlankInfo = {
  score: number;
  pickedAnswer?: string;
  color?: string;
};

type Unit = TextOrderingUnit & {
  wordIndex: number;
  unitIndex: number;
  unitGroup: number;
  unitGroupItemIndex: number;
  wordTtsAudio?: Uuid;
};

type MemoryGameStoreState = {
  gameController: GameController | undefined;
  modifiedAnswer: TextOrderingAnswer | undefined;
  blankUnits: Unit[] | undefined;
  mode: TextOrderingGameMode;
  locale: TtsLocale;
  isPinyin: boolean;
  useColor: boolean;
  correctAnswersPerHand: number;
  currentBlankIndex: number;
  preFilledIndexes: number[][][];
  isEnded: boolean;
  blankInfos: Record<number, TextOrderingBlankInfo>;
  cards: AnswerCardData[];
  hintUsed: boolean;
  setEvaluation:
    | ((blankInfos: Record<number, TextOrderingBlankInfo>) => void)
    | undefined;
  isHintDisabled: boolean;
};

type MemoryGameStoreAction = {
  setup: (
    gameController: GameController | undefined,
    answer: TextOrderingAnswer,
    questionTtsFileId: Uuid | undefined,
    isAssistedGame: boolean | undefined,
    isRevisionGame: boolean | undefined,
    evaluationType: EvaluationType,
  ) => Promise<void>;
  cleanup: () => void;
  refreshCards: () => void;
  onHint: () => string | undefined;
  onSwap: () => boolean;
  onAnswer: (pickedCard: AnswerCardData, isPreFilled?: boolean) => boolean;
  updateCardStatus: (card: AnswerCardData, newStatus: AnswerCardStatus) => void;
  forceUpdateCardStatusesAfterAnswer: (
    pickedCard: AnswerCardData,
    isCorrect: boolean,
  ) => void;
};

type Store = MemoryGameStoreState & MemoryGameStoreAction;

const INITIAL_VALUE: MemoryGameStoreState = {
  gameController: undefined,
  modifiedAnswer: undefined,
  blankUnits: undefined,
  mode: TextOrderingGameMode.QuickMemory,
  locale: TtsLocale.US,
  isPinyin: false,
  useColor: false,
  correctAnswersPerHand: 1,
  currentBlankIndex: -1,
  preFilledIndexes: [],
  isEnded: false,
  blankInfos: {},
  cards: [],
  hintUsed: false,
  setEvaluation: undefined,
  isHintDisabled: true,
};

const getScores = (blankUnits: Unit[] | undefined) => {
  const answerCount = blankUnits?.length ?? 0;
  const baseScore = answerCount === 0 ? 0 : Math.ceil(60 / answerCount);
  const wrongPenalty = Math.min(baseScore * 2, 20);
  const hintPenalty = Math.min(baseScore, 10);
  const swapPenalty = 15;
  return { baseScore, wrongPenalty, hintPenalty, swapPenalty };
};

export const useMemoryGameStore = create<Store>((set, get) => {
  const onEnd = () => {
    set({ isEnded: true });
    get()
      .gameController?.setGamePlayState(GamePlayState.Score)
      .catch(console.error);
  };

  const tempDisableSwap = () => {
    set({ isHintDisabled: true });
    setTimeout(() => {
      set({ isHintDisabled: false });
    }, 700);
  };

  return {
    ...INITIAL_VALUE,
    //
    setup: async (
      gameController: GameController | undefined,
      answer: TextOrderingAnswer,
      questionTtsFileId: Uuid | undefined,
      isAssistedGame: boolean | undefined,
      isRevisionGame: boolean | undefined,
      evaluationType: EvaluationType,
    ) => {
      tempDisableSwap();
      let modifiedAnswer = answer;
      if (evaluationType === EvaluationType.InApp) {
        const originalAnswer = cloneDeep(answer);
        const splitWords: string[] = [];
        const wordWeights: number[] = [];
        let wordCount = 0;
        originalAnswer.words.forEach((word, i) => {
          word.units.forEach((unit, j) => {
            splitWords.push(`${i}_${j}`);
            wordWeights.push(
              unit.type === TextOrderingUnitType.Candidate
                ? unit.key.length
                : 0,
            );
            if (unit.type === TextOrderingUnitType.Candidate) {
              wordCount += 1;
            }
          });
        });
        if (wordCount > 0) {
          const indexes = selectRandomWords(
            splitWords,
            wordWeights,
            getCandidateAnswerSlot(wordCount, modifiedAnswer.useColor),
          );

          originalAnswer.words.forEach((word, i) => {
            word.units.forEach((unit, j) => {
              if (
                indexes.find(
                  (selectedIndex) => splitWords[selectedIndex] === `${i}_${j}`,
                ) !== undefined
              ) {
                unit.type = TextOrderingUnitType.Blank;
              } else {
                unit.type = TextOrderingUnitType.Hint;
              }
            });
          });
          modifiedAnswer = originalAnswer;
        }
      }

      const blankUnits = modifiedAnswer.words
        .flatMap((word, wordIndex) => {
          // grouping consecutive hints or blanks as an unit group
          // for finding real 'word' (consecutive blanks) in syallable game
          let unitGroup = 0;
          let unitGroupItemIndex = 0; // Track index within the same group
          return word.units.map((unit, unitIndex) => {
            if (
              unitIndex > 0 &&
              unit.type !== word.units[unitIndex - 1]!.type
            ) {
              unitGroup += 1;
              unitGroupItemIndex = 0; // Reset index for the new group
            }

            const _result = {
              ...unit,
              wordIndex,
              unitIndex,
              unitGroup,
              unitGroupItemIndex,
              wordTtsAudio: isAssistedGame // || isRevisionGame
                ? word.ttsAudio
                : questionTtsFileId ||
                  modifiedAnswer.soundMode === TextOrderingSoundMode.Silent
                ? undefined
                : word.ttsAudio,
            };

            if (unit.type === TextOrderingUnitType.Blank) {
              unitGroupItemIndex += 1; // Increment only for blanks
            }

            return _result;
          });
        })
        .filter((unit) => unit.type === TextOrderingUnitType.Blank);

      let mode: TextOrderingGameMode;
      if (modifiedAnswer.answerFirstLetter) {
        mode = TextOrderingGameMode.QuickMemory;
      } else if (
        modifiedAnswer.useColor ||
        blankUnits.some((unit) => getCharacterCount(unit.key) > 1)
      ) {
        mode = TextOrderingGameMode.Syllable;
      } else {
        mode = TextOrderingGameMode.Spelling;
      }

      let correctAnswersPerHand: number;
      if (blankUnits.length <= 1) {
        correctAnswersPerHand = 1;
      } else if (blankUnits.length <= 3) {
        correctAnswersPerHand = 2;
      } else if (blankUnits.length <= 4) {
        correctAnswersPerHand = 2 + Math.round(Math.random());
      } else if (blankUnits.length <= 6) {
        correctAnswersPerHand = 3 + Math.round(Math.random());
      } else if (blankUnits.length <= 8) {
        correctAnswersPerHand = 4 + Math.round(Math.random());
      } else if (blankUnits.length <= 10) {
        correctAnswersPerHand = 5 + Math.round(Math.random());
      } else {
        correctAnswersPerHand = 6 + Math.round(Math.random());
      }

      const setEvaluation = (
        blankInfos: Record<number, TextOrderingBlankInfo>,
      ) => {
        const evaluation = modifiedAnswer.words.map(
          (word) =>
            new TextEvaluationWord({
              word: word.word,
              records: [
                {
                  units: word.units.map((unit) => ({
                    unit: unit.key,
                    isBlank: unit.type === TextOrderingUnitType.Blank,
                  })),
                },
              ],
            }),
        );

        Object.entries(blankInfos).forEach(([blankIndex, blankInfo]) => {
          const unit = blankUnits[blankIndex as unknown as number];
          if (unit) {
            evaluation[unit.wordIndex]?.records[0]?.units[
              unit.unitIndex
            ]?.setScore(blankInfo.score);
          }
        });

        gameController?.setInAppEvaluation({
          word: evaluation,
          type: evaluationType,
        });
      };

      set({
        gameController,
        modifiedAnswer,
        blankUnits,
        locale: modifiedAnswer.locale,
        mode,
        isPinyin: false, // TODO
        useColor: modifiedAnswer.useColor ?? false,
        correctAnswersPerHand,
        currentBlankIndex: 0,
        preFilledIndexes:
          mode === TextOrderingGameMode.Spelling
            ? groupByWordIndexAndUnitGroupWithPreFilledIndexes(blankUnits)
            : [],
        isEnded: false,
        blankInfos: {},
        cards: [],
        hintUsed: false,
        setEvaluation,
      });

      gameController?.startStage(blankUnits.length);
      setEvaluation({});
      await gameController?.setGamePlayState(GamePlayState.Run);
      await gameController?.setGamePlayState(GamePlayState.Running);
      if (isAssistedGame /* || isRevisionGame */) {
        gameController?.playAudio(blankUnits[0]?.wordTtsAudio);
      } else {
        gameController?.playAudio(
          questionTtsFileId ?? blankUnits[0]?.wordTtsAudio,
        );
      }
    },
    cleanup: () => {
      set(INITIAL_VALUE);
    },
    refreshCards: () => {
      const {
        blankUnits,
        mode,
        locale,
        isPinyin,
        useColor,
        correctAnswersPerHand,
        currentBlankIndex,
      } = get();
      if (blankUnits) {
        const currentBlankUnit = blankUnits[currentBlankIndex];
        if (currentBlankUnit) {
          let cardValues: string[];
          if (mode === TextOrderingGameMode.Syllable) {
            cardValues = generateSyllableCards(
              blankUnits,
              currentBlankUnit.wordIndex,
              currentBlankUnit.unitGroup,
            );
          } else {
            const correctAnswers = blankUnits
              .slice(
                currentBlankIndex,
                currentBlankIndex + correctAnswersPerHand,
              )
              .map((unit) => unit.key[0] as string);

            cardValues = generateLetterCards(
              correctAnswers,
              12,
              locale,
              isPinyin,
            );
          }
          const randomColors = shuffle(Array.from({ length: 12 }, (_, i) => i));
          set({
            cards: shuffle(
              cardValues.map((value, i) => ({
                value,
                status: AnswerCardStatus.Pending,
                color: useColor
                  ? colorForStroke(
                      mode === TextOrderingGameMode.Syllable
                        ? i
                        : randomColors[i] ?? i,
                    )
                  : undefined,
              })),
            ),
          });
        }
      }
    },
    onHint: () => {
      const {
        gameController,
        blankUnits,
        mode,
        currentBlankIndex,
        cards,
        hintUsed,
      } = get();
      const currentBlankUnit = blankUnits?.[currentBlankIndex];
      if (!hintUsed && currentBlankUnit) {
        const expectedAnswer =
          mode === TextOrderingGameMode.QuickMemory
            ? currentBlankUnit.key[0]
            : currentBlankUnit.key;

        const indexes: number[] = [];
        cards.map((card, i) => {
          if (card.value === expectedAnswer) {
            indexes.push(i);
          }
        });
        const index = indexes[Math.floor(Math.random() * indexes.length)];

        const correctAnswers = cards.reduce((acc, cur) => {
          if (
            cur.value === expectedAnswer &&
            cur.status !== AnswerCardStatus.Empty
          ) {
            return acc + 1;
          }
          return acc;
        }, 0);

        if (correctAnswers) {
          const updatedCards = cards.map((card, i) =>
            i === index
              ? // ||
                //   Math.random() < (mode === TextOrderingGameMode.Syllable ? 0 : 0.5) // syllable mode hint always make all wrong ans go away
                card
              : {
                  ...card,
                  status: [
                    AnswerCardStatus.Pending,
                    AnswerCardStatus.SwapPending,
                    AnswerCardStatus.Wrong,
                    AnswerCardStatus.Correct,
                  ].includes(card.status)
                    ? card.status === AnswerCardStatus.Correct
                      ? AnswerCardStatus.Empty
                      : AnswerCardStatus.HintCrossed
                    : card.status,
                },
          );
          set({ cards: updatedCards, hintUsed: true });
        } else {
          set({ hintUsed: true });
        }

        const { hintPenalty } = getScores(blankUnits);
        if (gameController?.addScore(-hintPenalty) === 0) {
          onEnd();
        }
        if (mode === TextOrderingGameMode.Syllable) {
          if (
            cards.reduce(
              (acc, cur) =>
                cur.status !== AnswerCardStatus.Empty ? acc + 1 : acc,
              0,
            ) === 1
          ) {
            return 'No incorrect answer here.';
          }
        } else {
          if (correctAnswers === 0) {
            return 'No correct answer here.';
          }
        }
      }
      return undefined;
    },
    onSwap: () => {
      const {
        gameController,
        blankUnits,
        mode,
        currentBlankIndex,
        blankInfos,
        cards,
        setEvaluation,
      } = get();
      set({ hintUsed: false });
      tempDisableSwap();
      const currentBlankUnit = blankUnits?.[currentBlankIndex];
      const currentAnswer =
        mode === TextOrderingGameMode.QuickMemory
          ? currentBlankUnit?.key[0]
          : currentBlankUnit?.key;

      const hasCorrectAnswers = cards.some(
        (card) =>
          card.status === AnswerCardStatus.Pending &&
          card.value === currentAnswer,
      );
      if (hasCorrectAnswers) {
        const currentInfo = blankInfos[currentBlankIndex] ?? {
          score: 100,
        };
        const newBlankInfos = {
          ...blankInfos,
          [currentBlankIndex]: {
            score: Math.max(currentInfo.score - 30, 0),
          },
        };

        const updatedCards = cards.map((card) => ({
          ...card,
          status: [AnswerCardStatus.Pending].includes(card.status)
            ? AnswerCardStatus.SwapPending
            : card.status,
        }));
        set({
          cards: updatedCards,
          blankInfos: newBlankInfos,
        });

        setEvaluation?.(newBlankInfos);

        const { swapPenalty } = getScores(blankUnits);
        if (gameController?.addScore(-swapPenalty) === 0) {
          onEnd();
        }
      } else {
        set({ cards: [] });
      }

      return !hasCorrectAnswers;
    },
    onAnswer: (pickedCard: AnswerCardData, isPreFilled?: boolean) => {
      const {
        gameController,
        blankUnits,
        mode,
        currentBlankIndex,
        blankInfos,
        setEvaluation,
        // updateCardStatus,
        forceUpdateCardStatusesAfterAnswer,
      } = get();
      const currentBlankUnit = blankUnits?.[currentBlankIndex];
      const currentAnswer =
        mode === TextOrderingGameMode.QuickMemory
          ? currentBlankUnit?.key[0]
          : currentBlankUnit?.key;

      const isCorrect =
        Boolean(currentAnswer) && pickedCard.value === currentAnswer;

      // updateCardStatus(
      //   pickedCard,
      //   isCorrect ? AnswerCardStatus.Correct : AnswerCardStatus.Wrong,
      // );
      if (!isPreFilled) {
        forceUpdateCardStatusesAfterAnswer(pickedCard, isCorrect);
      }

      const currentInfo = blankInfos[currentBlankIndex] ?? {
        score: 100,
      };
      const newBlankInfos = {
        ...blankInfos,
        [currentBlankIndex]: isCorrect
          ? {
              score: currentInfo.score,
              pickedAnswer: pickedCard.value,
              color: pickedCard.color,
            }
          : {
              score: Math.max(currentInfo.score - 30, 0),
            },
      };
      set({ blankInfos: newBlankInfos });
      setEvaluation?.(newBlankInfos);

      const answerCount = blankUnits?.length ?? 0;
      const { baseScore, wrongPenalty } = getScores(blankUnits);

      if (isCorrect) {
        if (!isPreFilled) {
          gameController?.playAudio(InGameSoundEffects.correctSelection);
        }
        const newCards = get().cards;
        const removeCross = newCards.map((card) => ({
          // this is for syllable case only, since store update seems has delay
          ...card,
          status:
            card.status === AnswerCardStatus.HintCrossed
              ? AnswerCardStatus.Pending
              : card === pickedCard
              ? isCorrect
                ? AnswerCardStatus.Correct
                : AnswerCardStatus.Wrong
              : card.status,
        }));
        const nextIndex = currentBlankIndex + 1;
        set({
          currentBlankIndex: nextIndex,
          hintUsed: false,
        });
        if (!isPreFilled) {
          gameController?.addScore(baseScore);
        }

        gameController?.addStagePoint(1);

        if (nextIndex < answerCount) {
          gameController?.initializeNewLap(Infinity);

          if (
            blankUnits?.[currentBlankIndex]?.wordTtsAudio !==
            blankUnits?.[nextIndex]?.wordTtsAudio
          ) {
            gameController?.playAudio(blankUnits?.[nextIndex]?.wordTtsAudio);
          }

          // since syallable game only showing card for current 'word'
          // need to auto refresh cards when 'word' completed
          if (mode === TextOrderingGameMode.Syllable) {
            const nextAnswer = blankUnits?.[nextIndex]?.key;
            const hasCorrectAnswers = removeCross.some(
              (card) =>
                card.status === AnswerCardStatus.Pending &&
                card.value === nextAnswer,
            );
            if (!hasCorrectAnswers) {
              setTimeout(() => {
                tempDisableSwap();
                set({ cards: [] });
              }, 1000);
            } else {
              // set({ cards: removeCross });
            }
          }
        } else {
          setTimeout(() => {
            // delay end to wait for animation
            onEnd();
          }, 800);
        }
      } else if (gameController?.addScore(-wrongPenalty) === 0) {
        onEnd();
      } else {
        gameController?.playAudio(InGameSoundEffects.wrongSelection);
      }

      return isCorrect;
    },
    updateCardStatus: (card: AnswerCardData, newStatus: AnswerCardStatus) => {
      const { cards } = get();
      const updatedCards = cards.map((c) =>
        c === card ? { ...c, status: newStatus } : c,
      );
      set({ cards: updatedCards });
    },
    forceUpdateCardStatusesAfterAnswer: (
      pickedCard: AnswerCardData,
      isCorrect: boolean,
    ) => {
      const { cards } = get();
      const updatedCards = cards.map((card) => {
        if (card === pickedCard) {
          return {
            ...card,
            status: isCorrect
              ? AnswerCardStatus.Correct
              : AnswerCardStatus.Wrong,
          };
        }

        switch (card.status) {
          case AnswerCardStatus.Correct:
            return { ...card, status: AnswerCardStatus.Empty };
          case AnswerCardStatus.Wrong:
            return { ...card, status: AnswerCardStatus.Pending };
          case AnswerCardStatus.Pending:
            return { ...card, status: AnswerCardStatus.Pending };
          case AnswerCardStatus.Empty:
            return { ...card, status: AnswerCardStatus.Empty };
          case AnswerCardStatus.HintCrossed:
            return { ...card, status: AnswerCardStatus.Pending };
          case AnswerCardStatus.SwapPending:
            return { ...card, status: AnswerCardStatus.Pending };
          default:
            return card;
        }
      });
      set({ cards: updatedCards });
    },
  };
});

export type MemoryGameMainGroupProps = {
  gamePlayState: GamePlayState;
  gameController?: GameController;
  answer: TextOrderingAnswer;
  questionTtsFileId: Uuid | undefined;
  isAssistedGame?: boolean;
  isRevisionGame?: boolean;
  evaluationType: EvaluationType;
};

export const MemoryGameMainGroup = memo(
  ({
    gamePlayState,
    gameController,
    answer,
    questionTtsFileId,
    isAssistedGame,
    isRevisionGame,
    evaluationType,
  }: MemoryGameMainGroupProps) => {
    const {
      // modifiedAnswer,
      blankUnits,
      mode,
      currentBlankIndex,
      preFilledIndexes,
      isEnded,
      // blankInfos,
      cards,
      hintUsed,
      // useColor,
      setup,
      cleanup,
      refreshCards,
      onHint,
      onSwap,
      onAnswer,
      updateCardStatus,
      isHintDisabled,
    } = useMemoryGameStore();
    const { t } = useTranslation();
    const { showPopup, hidePopup } = usePopup();

    useEffect(() => {
      if (gamePlayState === GamePlayState.Start && !blankUnits) {
        setup(
          gameController,
          answer,
          questionTtsFileId,
          isAssistedGame,
          isRevisionGame,
          evaluationType,
        ).catch(console.error);
      }
    }, [
      gamePlayState,
      gameController,
      answer,
      questionTtsFileId,
      isAssistedGame,
      isRevisionGame,
      evaluationType,
      blankUnits,
      setup,
    ]);

    useEffect(() => {
      return cleanup;
    }, [cleanup]);

    const currentBlankUnit = blankUnits?.[currentBlankIndex];

    useEffect(() => {
      // MARK: only for spelling standard mode
      if (currentBlankUnit && mode === TextOrderingGameMode.Spelling) {
        const wI = currentBlankUnit.wordIndex;
        const uI = currentBlankUnit.unitGroup;
        const uGI = currentBlankUnit.unitGroupItemIndex;
        const isPreFilledBlank =
          wI !== undefined &&
          uI !== undefined &&
          uGI !== undefined &&
          Array.isArray(preFilledIndexes[wI]) &&
          preFilledIndexes[wI]?.[uI]?.includes(uGI);

        if (isPreFilledBlank) {
          // MARK: auto answer if is pre-filled blank
          onAnswer(
            {
              value: currentBlankUnit.key,
              status: AnswerCardStatus.Pending,
            },
            true,
          );
        }
      }
    }, [currentBlankUnit, mode, onAnswer, preFilledIndexes]);

    useEffect(() => {
      if (cards.length === 0 && currentBlankUnit && !isEnded) {
        refreshCards();
      }
    }, [cards, currentBlankUnit, isEnded, refreshCards]);

    const handleHintClick = useCallback(() => {
      const toastMessage = onHint();
      if (toastMessage) {
        toast(t(toastMessage), {
          ...AppToastGameErrorOption,
          id: 'HintCard',
        });
      }
    }, [onHint, t]);

    if (!blankUnits) {
      return <></>;
    }

    if (blankUnits && !blankUnits.length) {
      return <div>Error: Game question data is undefined or empty.</div>;
    }

    let tutorialCard: AnswerCardData | undefined;
    if (currentBlankUnit && isAssistedGame) {
      tutorialCard = cards.find((card) => {
        const expectedAnswer =
          mode === TextOrderingGameMode.QuickMemory
            ? currentBlankUnit.key[0]
            : currentBlankUnit.key;
        return (
          card.value === expectedAnswer &&
          (card.status === AnswerCardStatus.Pending ||
            card.status === AnswerCardStatus.SwapPending)
        );
      });
    }

    const handleSwapClick = () => {
      const isCorrect = onSwap();
      if (!isCorrect) {
        toast(t('Still has correct answer!'), {
          ...AppToastGameErrorOption,
          id: 'swapCard',
        });
        gameController?.playAudio(InGameSoundEffects.wrongSelection);
      } else {
        gameController?.playAudio(InGameSoundEffects.flipCard);
      }
    };

    const onClickCancelExit = () => {
      hidePopup();
      gameController
        ?.setGamePlayState(GamePlayState.Resume)
        .catch(console.error);
    };

    return (
      <div className={styles.container}>
        {/* {modifiedAnswer && (
          <GameQuestionPanel
            answer={modifiedAnswer}
            currentBlankIndex={currentBlankIndex}
            spellingPreFilledIndexes={preFilledIndexes}
            blankInfos={blankInfos}
            mode={mode}
            useColor={useColor}
          />
        )} */}

        <div
          className={`${styles.answerCards} ${
            isEnded || !currentBlankUnit ? styles.disabled : ''
          }`}
        >
          <GameBlankAnswerCardsGroup
            cards={cards}
            onCardClick={onAnswer}
            onUpdateCardStatus={updateCardStatus}
            tutorialCard={tutorialCard}
          />
        </div>

        <div className={styles.buttonBar}>
          <div
            className={styles.back}
            onClick={() => {
              gameController
                ?.setGamePlayState(GamePlayState.Pause)
                .catch(console.error);
              showPopup(
                <LeaveGamePopup
                  onClickCancel={onClickCancelExit}
                  onClickLeave={() => {
                    history.back();
                    hidePopup();
                    // gameController
                    //   ?.setGamePlayState(GamePlayState.Resume)
                    //   .catch(console.error);
                  }}
                />,
                { backdropCallback: () => onClickCancelExit() },
              );
            }}
          >
            <div className={styles.logoutContainer}>
              <SvgIcon
                name={SVGIconType.Logout}
                // height={'100%'}
                // width={'100%'}
                color={'white'}
                style={{ minHeight: '0' }}
              />
            </div>
          </div>
          {!isEnded && (
            <>
              <HintButton
                onClick={handleHintClick}
                disabled={isAssistedGame || hintUsed || isHintDisabled}
                scoreCost={getScores(blankUnits).hintPenalty}
              />
              {mode !== TextOrderingGameMode.Syllable && (
                <SwapCardsButton
                  onClick={handleSwapClick}
                  showTutorial={Boolean(
                    currentBlankUnit && isAssistedGame && !tutorialCard,
                  )}
                  isDisabled={isHintDisabled}
                />
              )}
            </>
          )}
        </div>
      </div>
    );
  },
);

const HintButton: React.FC<{
  onClick: () => void;
  disabled: boolean;
  scoreCost: number;
}> = ({ onClick, disabled, scoreCost = 5 }) => {
  const { t } = useTranslation();
  return (
    <div
      className={
        !disabled
          ? styles.hint
          : `${styles.hint} ${styles.disabled} ${styles.halfOpacity}`
      }
      onClick={!disabled ? onClick : undefined}
    >
      <div className={styles.icon}>
        <img src={Images.JOYAJOY_logo} width={40} height={40} />
      </div>
      <div className={styles.text}>
        <span className={styles.label}>{t('Hint')}</span>
        <span className={styles.scoreDeduction}>-{scoreCost}</span>
      </div>
    </div>
  );
};

const SwapCardsButton: React.FC<{
  onClick: () => void;
  showTutorial: boolean;
  isDisabled?: boolean;
}> = ({ onClick, showTutorial, isDisabled }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`${styles.swap} ${
        isDisabled ? `${styles.halfOpacity} ${styles.disabled}` : ''
      }`}
      onClick={isDisabled ? undefined : onClick}
    >
      <div className={styles.icon}>
        <PngIcon name="GameSwap" width={32} height={32} />
        {showTutorial && (
          <motion.img
            src={Images.HandwriteFinger}
            alt="Finger pointing"
            className={styles.fingerOverlay}
            // initial={{ x: 10, y: 10 }}
            animate={{ x: [4, -4], y: [4, -4] }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              repeatType: 'reverse',
            }}
          />
        )}
      </div>

      <span className={styles.label}>{t('Swap')}</span>
    </div>
  );
};

// move to const
export const generateSyllableCards = (
  units: Unit[],
  wordIndex: number,
  unitGroup: number,
) => {
  const cards: string[] = [];

  units.forEach((unit) => {
    if (
      unit.type === TextOrderingUnitType.Blank &&
      unit.wordIndex === wordIndex &&
      unit.unitGroup === unitGroup
    ) {
      cards.push(unit.key);
    }
  });

  return cards;
};

export const generateLetterCards = (
  correctAnswers: string[],
  count: number,
  locale: TtsLocale,
  isPinyin: boolean,
) => {
  const distractorPools = getDistractorPools(locale);
  const confusion1 = ['1', 'i', 'I', 'l', 'L'];
  const confusion2 = ['0', 'o', 'O'];

  let exclusions: string[] = [];

  // remove confusion answers
  const answers: string[] = [];
  for (let i = 0; i < correctAnswers.length; i += 1) {
    const answer = correctAnswers[i] as string;
    if (exclusions.includes(answer)) {
      break;
    }

    answers.push(answer);

    if (confusion1.includes(answer)) {
      exclusions = exclusions.concat(confusion1.filter((c) => c !== answer));
    }
    if (confusion2.includes(answer)) {
      exclusions = exclusions.concat(confusion2.filter((c) => c !== answer));
    }
  }

  // create distractor pools
  let pool: string[] = [];

  answers.forEach((answer) => {
    // pool should also contains the correct answers
    pool.push(answer);

    // use pool if contains answer first
    const matchingPool = distractorPools.find((p) => p.pool.includes(answer));
    if (matchingPool) {
      pool = pool.concat(matchingPool.pool);
    } else {
      // if no pool contains answer, then use pools matching isPinyin with mustContains = false.
      // if no such pool, then use pools matching isPinyin
      // if no such pool, then use all pools
      // let pools = distractorPools.filter(
      //   (p) => p.isPinyin === isPinyin && !p.mustContains,
      // );
      // if (pools.length === 0) {
      //   pools = distractorPools.filter((p) => p.isPinyin);
      // }
      // if (pools.length === 0) {
      //   pools = distractorPools;
      // }
      // pool = pool.concat(...pools.map((p) => p.pool));
    }
  });

  if (pool.length <= answers.length) {
    let pools = distractorPools.filter(
      (p) => p.isPinyin === isPinyin && !p.mustContains,
    );
    if (pools.length === 0) {
      pools = distractorPools.filter((p) => p.isPinyin);
    }
    if (pools.length === 0) {
      pools = distractorPools.filter((p) => p.basicPool);
    }
    if (pools.length === 0) {
      pools = distractorPools;
    }

    pool = pool.concat(...pools.map((p) => p.pool));
  }

  const uniquePool = new Set(pool);
  exclusions.forEach((e) => uniquePool.delete(e));
  pool = [...uniquePool];

  const cards = [...new Set(answers)];
  while (cards.length < count) {
    // pool should not be empty because having the answers themselves
    const distractor = pool[Math.floor(Math.random() * pool.length)] as string;
    if (pool.length < count || !cards.includes(distractor)) {
      cards.push(distractor);
    }

    // remove confusion letter from pool
    if (confusion1.includes(distractor)) {
      confusion1.forEach((c) => {
        if (c !== distractor) {
          uniquePool.delete(c);
        }
      });
      pool = [...uniquePool];
    }
    if (confusion2.includes(distractor)) {
      confusion2.forEach((c) => {
        if (c !== distractor) {
          uniquePool.delete(c);
        }
      });
      pool = [...uniquePool];
    }
  }

  return cards;
};

// MARK: Pre-Filled Char in Spelling
type BlankUnitsType = {
  wordIndex: number;
  unitIndex: number;
  unitGroup: number;
  wordTtsAudio: string | undefined;
  key: string;
  type: TextOrderingUnitType;
  distractors?: 'none' | 'auto' | string[];
  ttsAudio?: Uuid;
};
const groupByWordIndexAndUnitGroupWithPreFilledIndexes = (
  data: BlankUnitsType[],
): number[][][] => {
  // Find the range of wordIndexes
  const wordIndexes = data.map((item) => item.wordIndex);
  const minWordIndex = Math.min(...wordIndexes);
  const maxWordIndex = Math.max(...wordIndexes);

  // Initialize the grouped structure for wordIndex
  const grouped: BlankUnitsType[][] = Array.from(
    { length: maxWordIndex - minWordIndex + 1 },
    () => [],
  );

  // Populate the wordIndex groups
  data.forEach((item) => {
    const wordIndex = item.wordIndex - minWordIndex; // Adjust for 0-based indexing

    // Use the spread operator to safely handle undefined
    grouped[wordIndex] = [...(grouped[wordIndex] || []), item];
  });

  // Now group within each wordIndex group by unitGroup
  const fullyGrouped = grouped.map((wordGroup) => {
    // If the wordGroup is empty, return an array of empty arrays
    if (wordGroup.length === 0) return [];

    // Find the range of unitGroups in this wordGroup
    const unitGroups = wordGroup.map((item) => item.unitGroup);
    const minUnitGroup = 0; // Start from 0 explicitly
    const maxUnitGroup = Math.max(...unitGroups);

    // Initialize an array for unitGroups (starting from 0)
    const unitGroupArray: BlankUnitsType[][] = Array.from(
      { length: maxUnitGroup - minUnitGroup + 1 },
      () => [],
    );

    // Populate the unitGroup arrays
    wordGroup.forEach((item) => {
      const unitGroupIndex = item.unitGroup - minUnitGroup; // Adjust for 0-based indexing

      // Use the spread operator to safely handle undefined
      unitGroupArray[unitGroupIndex] = [
        ...(unitGroupArray[unitGroupIndex] || []),
        item,
      ];
    });

    // Replace each unitGroup with the result of getPreFilledIndexes or an empty array if empty
    return unitGroupArray.map((unitGroup) =>
      unitGroup.length > 0 ? getPreFilledIndexes(unitGroup.length) : [],
    );
  });

  return fullyGrouped;
};
